import { z } from "zod";
import { publicationPricingInputSchema } from "./acquisition-publication.js";
import { goodsOriginSchema, ufSchema } from "./calculation.js";
import { uploadPriceCompositionSchema } from "./price-composition.js";
/** Largest sheet the calculator reads: a BackChannel scenario sheet is ~85 columns wide. */
export const LOT_MAX_COLUMNS = 200;
/** Physical rows of one sheet: the data rows plus a title block, the header and footnotes. */
export const LOT_MAX_SHEET_ROWS = 5025;
export const LOT_MAX_SHEETS = 40;
/**
 * Product rows the calculator imports from one sheet. Measured against the gateway's 60 s
 * workbook deadline and the 1 GiB admin API; see docs/calculations/onil-lot-simulation.md.
 */
export const LOT_MAX_DATA_ROWS = 5000;
/**
 * Calculator input fields a spreadsheet column can feed. `expected_*` columns
 * are the workbook's own results, kept only to compare with the calculator.
 * `ref_*` columns are rates the workbook states; they are shown for review and
 * never become a rule, an aliquota or a calculation input.
 */
export const lotFieldSchema = z.enum([
    "item_id",
    "sku",
    "descricao",
    "category",
    // Product facts the file states (brand, pricing family, EAN, weight): display only, never a
    // calculation input. They ride on the imported row, outside the item and its request hash.
    "marca",
    "familia",
    "ean",
    "peso",
    "ncm",
    "cest",
    "origem_mercadoria",
    "uf_origem",
    "uf_destino",
    "custo",
    "quantidade",
    "base_valor",
    "unidade_comercial",
    "tipo_fornecedor",
    "st_pago_anteriormente",
    "situacao_st_compra",
    "base_preco",
    "preco_venda",
    "uf_destino_venda",
    "tipo_comprador",
    "regime_pis_cofins",
    "deposito",
    "validade",
    "meio_pagamento",
    "tipo_transacao",
    "margem_alvo",
    "markup",
    "expected_ipi",
    "expected_icms_st",
    "expected_tax_total",
    "expected_landed",
    "expected_own_icms",
    "expected_purchase_pis_cofins",
    "expected_workbook_cost",
    "expected_sale_price",
    "expected_sale_icms",
    "expected_sale_pis_cofins",
    "expected_sale_st_fcp",
    "expected_buyer_invoice",
    "ref_cfop",
    "ref_mva",
    "ref_aliq_ipi",
    "ref_aliq_icms",
    "ref_pis_cofins",
    "ref_frete",
]);
/** Additive persisted fields: read them without admitting them to the legacy converter. */
export const LOT_STORED_TEMPLATE_FIELDS = [
    "situacao_st_compra",
    "regime_pis_cofins",
    "preco_venda",
    "uf_destino_venda",
    "tipo_comprador",
    "deposito",
    "validade",
    "base_preco",
];
export const lotStoredFieldSchema = z.enum([
    ...lotFieldSchema.options,
    ...LOT_STORED_TEMPLATE_FIELDS,
]);
/**
 * How the origin → destination UF of the whole file is declared.
 * FIXED: one route for every row. DYNAMIC: each row states its own route in
 * the mapped UF columns; `originUf` / `destinationUf` are the fallback for a
 * side whose column is not mapped or whose cell is empty.
 */
export const lotRouteSchema = z.discriminatedUnion("mode", [
    z
        .object({
        mode: z.literal("FIXED"),
        originUf: ufSchema,
        destinationUf: ufSchema,
    })
        .strict(),
    z
        .object({
        mode: z.literal("DYNAMIC"),
        originUf: ufSchema.optional(),
        destinationUf: ufSchema.optional(),
    })
        .strict(),
]);
/** What to do when the file needs a route the platform has no rule for. Stored only. */
export const researchMissingSchema = z.enum(["OFF", "ASK", "AUTO"]);
/** Human declarations for a commercial workbook that lacks canonical columns.
 * Blank row cells may use these values; nonempty row declarations always win.
 * UNKNOWN and invoice-retained ST remain blocked for review by the converter. */
export const lotCommercialDeclarationSchema = z
    .object({
    amountBasis: z.enum(["UNIT", "LINE_TOTAL", "UNKNOWN"]),
    commercialUnit: z.string().trim().max(200),
    priceBasis: z.enum(["GOODS_BEFORE_IPI_ST", "ACQUISITION_INCLUDES_TAXES", "UNKNOWN"]),
    purchaseStStatus: z.enum(["NEW", "PRIOR_ST_INCLUDED", "INVOICE_RETAINED", "UNKNOWN"]),
    pisCofinsRegime: z.enum(["CUMULATIVE", "NON_CUMULATIVE", "UNKNOWN"]),
    /** Explicit upload declaration for blank origin cells only; never overrides a populated cell. */
    goodsOrigin: goodsOriginSchema.optional(),
})
    .strict();
export const lotMappingSchema = z
    .object({
    sheet: z.string().min(1).max(100),
    /** Explicit fixed-template contract; omitted for retained/technical generic imports. */
    templateProfile: z.literal("JAMERSON_V1").optional(),
    headerRow: z.number().int().min(1).max(25),
    columns: z.partialRecord(lotFieldSchema, z
        .number()
        .int()
        .min(0)
        .max(LOT_MAX_COLUMNS - 1)),
    decimalSeparator: z.enum([",", "."]).default(","),
    originUf: ufSchema.optional(),
    destinationUf: ufSchema.optional(),
    /** Explicit file-level declaration. A nonempty row declaration takes precedence; UNKNOWN never falls back. */
    supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR"]).optional(),
    commercialDeclaration: lotCommercialDeclarationSchema.optional(),
    /** Acquisition quote before any buyer exists; enables exact bounded arithmetic for the mapped principal. */
    // Per-product supplier IPI is created from the fixed sheet's N/O cells, never a file-wide rate.
    publicationPricing: publicationPricingInputSchema
        .omit({ supplierIpi: true, receivedPrice: true })
        .optional(),
    priceComposition: uploadPriceCompositionSchema.optional(),
    catalogSelection: z
        .object({
        supplierId: z.string().uuid(),
        revisionId: z.string().uuid(),
        on: z.string().date(),
    })
        .strict()
        .optional(),
    acceptCachedFormulas: z.boolean().default(false),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.columns.custo === undefined)
        ctx.addIssue({
            code: "custom",
            path: ["columns", "custo"],
            message: "Select the fiscal unit cost column",
        });
    const columns = Object.values(value.columns);
    if (new Set(columns).size !== columns.length)
        ctx.addIssue({
            code: "custom",
            path: ["columns"],
            message: "A column may map to only one fiscal field",
        });
});
/** Read-only upload metadata from the newer commercial template release. */
export const lotStoredMappingSchema = lotMappingSchema.safeExtend({
    columns: z.partialRecord(lotStoredFieldSchema, z
        .number()
        .int()
        .min(0)
        .max(LOT_MAX_COLUMNS - 1)),
    supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR"]).optional(),
});
//# sourceMappingURL=lot-mapping.js.map